import type { ShopCurrency, ShopId } from "@highmarch/domain";
import { uiUrl } from "../portraits";
import { gemIcon, goldIcon, marksIcon, trophiesIcon } from "../hub/econArt";

/**
 * The shops' art (docs/SHOP.md §9.5; tools/art/shops): each shop's
 * hero — Captain Bram (the live guide model) behind the Mystery Merchant's table, before the Quartermaster's weapon
 * rack, in the Royal Steward's hall (art/interface/shops/<shop>_hero.webp: a still life under dramatic light in the
 * right half, tools/art/kit/heroes_bpy.py) — their keepers (art/interface/icons/shop_<keeper>.png: Bram in role, with
 * the merchant's scales, the quartermaster's medal on crossed swords, the steward's crown) and the tabs' icons
 * (art/interface/icons/shop_tab_<shop>.png). Markup only.
 */

export const MERCHANT_STALL = "shops/merchant_stall.webp";
/** The Shop tab's and the Shop sheet's storefront (shop redesign 2026-10-19; source tools/art/icons/shop_storefront.svg). */
export const STOREFRONT_ICON = "icons/shop_storefront@3x.png";
export const MERCHANT_PORTRAIT = "icons/shop_merchant.png";
/** The hero art and the header portrait per shop. */
export const SHOP_HERO: Readonly<Record<ShopId, string>> = { merchant: MERCHANT_STALL, event: "shops/event_hero.webp", vip: "shops/vip_hero.webp" };
/**
 * Where the hero crops its art (CSS object-position): the heroes are 780×440 but show a ~300 px band of it at phone
 * width; tools/art/kit/heroes_bpy.py frames Bram at 70 % across with his helmet in the band this crop keeps even on
 * a 375 × 667 phone, and the shop's wares on the table before him, as it frames the Deals pages.
 */
export const HERO_FOCUS = "70% 52%";
export const SHOP_PORTRAIT: Readonly<Record<ShopId, string>> = { merchant: MERCHANT_PORTRAIT, event: "icons/shop_quartermaster.png", vip: "icons/shop_steward.png" };

/** A currency's icon at `size` CSS px (MP-23: Trophies too, the Trophy Hall's prices and header pill). */
export function currencyIcon(currency: ShopCurrency | "trophies" | "gold", size = 20): string {
  return currency === "gems" ? gemIcon(size) : currency === "marks" ? marksIcon(size) : currency === "gold" ? goldIcon(size) : trophiesIcon(size);
}

/**
 * The rendered daylight banners (2026-10-20, tools/art/kit/daylight_bpy.py: the fortress on sunny hills, Captain Bram
 * on the right), one per Store page and Shop tab: `art/interface/banners/banner_<id>.webp`. Null where none is painted.
 */
const BANNER_SCENES = new Set(["starter", "builders", "daily", "weekly", "custom", "masters", "commander", "fund", "chapter", "armoury",
  "treasury", "gems", "cards", "gold", "pass", "welcome", "vip", "merchant", "event", "trophy", "skins"]);
export function bannerSceneUrl(id: string): string | undefined {
  return BANNER_SCENES.has(id) ? uiUrl(`banners/banner_${id}.webp`) : undefined;
}
